<template>
  <div>
    <!-- ===========================
         MODO “TELA NORMAL”
         =========================== -->
    <div v-if="!printMode">
      <h6>Aulas Experimentais => Matrículas</h6><p></p>
      <div class="row mb-3">
        <div class="col-auto">
          <label>Data Início:</label>
          <input type="date" v-model="dataInicioLocal" class="form-control" />
        </div>
        <div class="col-auto">
          <label>Data Fim:</label>
          <input type="date" v-model="dataFimLocal" class="form-control" />
        </div>
        <div class="col-auto align-self-end">
          <button @click="abrirPrint" class="btn btn-primary">Executar</button>
        </div>
      </div>

      <!-- KPIs só para visual na TELA (não entram no PDF) -->
      <div v-if="resumoTela" class="kpi-row nao-imprimir">
        <div class="kpi-card">
          <div class="kpi-title">Aulas Agendadas</div>
          <div class="kpi-value">{{ resumoTela.agendadas }}</div>
        </div>
        <div class="kpi-card">
          <div class="kpi-title">Compareceram</div>
          <div class="kpi-value">{{ resumoTela.compareceram }}</div>
        </div>
        <div class="kpi-card">
          <div class="kpi-title">Matrículas Realizadas</div>
          <div class="kpi-value">{{ resumoTela.matriculas }}</div>
        </div>
        <div class="kpi-card">
          <div class="kpi-title">Percentual</div>
          <div class="kpi-value">{{ formatarPercentual(resumoTela.percentual) }}</div>
        </div>
      </div>
    </div>

    <!-- ===========================
         MODO “IMPRESSÃO / PRINT”
         =========================== -->
    <div v-else>
      <p class="text-center" style="font-size: 14px;">
        ( {{ globalUtils.formatarData(dataInicio) }} a {{ globalUtils.formatarData(dataFim) }} )
      </p>

      <div v-if="loading">Carregando…</div>

      <div v-else>
        <!-- TABELA DE KPIs (entra no PDF) -->
        <table class="table table-bordered kpi-table">
          <thead>
            <tr>
              <th class="text-center">Aulas Agendadas</th>
              <th class="text-center">Compareceram</th>
              <th class="text-center">Matrículas Realizadas</th>
              <th class="text-center">Percentual</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td class="text-center">{{ resumo.agendadas }}</td>
              <td class="text-center">{{ resumo.compareceram }}</td>
              <td class="text-center">{{ resumo.matriculas }}</td>
              <td class="text-center">{{ formatarPercentual(resumo.percentual) }}</td>
            </tr>
          </tbody>
        </table>

        <!-- TABELA DE DETALHE (entra no PDF) -->
        <div v-if="detalhe.length">
          <table class="table table-striped">
            <thead>
              <tr>
                <th>Prospect</th>
                <th>Aluno</th>
                <th>Responsável</th>
                <th>Agendado</th>
                <th>Professor</th>
                <th>Compareceu</th>
                <th>Matriculou?</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, idx) in detalhe" :key="item.prospectId + '-' + idx">
                <td class="text-center">{{ item.prospectId }}</td>
                <td>{{ item.aluno }}</td>
                <td>{{ item.responsavel }}</td>
                <td class="text-center">
                  <!-- backend já devolve 'DD/MM/YYYY - HH:MM'; fallback se vier separado -->
                  {{ item.agendado || (globalUtils.formatarData(item.agendadoData) + ' - ' + (item.agendadoHora || '')) }}
                </td>
                <td class="text-center">{{ item.professor }}</td>
                <td class="text-center">{{ item.compareceu }}</td>
                <td class="text-center">{{ item.matriculou }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-else>
          <p>Nenhum registro encontrado para este período.</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import axios from '@/services/axios'
import globalUtils from '@/utils/globalUtils.js'

const props = defineProps({
  printMode:  { type: Boolean, default: false },
  dataInicio: String,
  dataFim:    String,
  printTitle: { type: String, default: 'Aulas Experimentais => Matrículas' },
  subtitle:   { type: String, default: '' }
})

const router = useRouter()

// Estados da TELA (inputs)
const dataInicioLocal = ref('')
const dataFimLocal    = ref('')

// Estados do PRINT
const loading = ref(false)
const resumo  = ref({ agendadas: 0, compareceram: 0, matriculas: 0, percentual: 0 })
const detalhe = ref([])

// Opcional: mostrar KPIs na tela após buscar (quando usuário já executou uma vez)
const resumoTela = ref(null)

// Buscar ao entrar em printMode (chamado pelo ReportPrintWrapper)
onMounted(async () => {
  if (!props.printMode) return
  if (!props.dataInicio || !props.dataFim) return
  await carregarDados(props.dataInicio, props.dataFim)
})

// Se quiser pré-visualizar KPIs na TELA quando o usuário digitar datas (sem abrir print),
// descomente o watch abaixo para fazer uma pré-busca ao mudar as datas.
// watch([dataInicioLocal, dataFimLocal], async ([di, df]) => {
//   if (di && df) {
//     const r = await fetchResumo(di, df)
//     resumoTela.value = r
//   }
// })

async function carregarDados(di, df) {
  loading.value = true
  try {
    const res = await axios.get('/gestao/relatorios/operacionais/aulas-experimentais-matriculas', {
      params: { dataInicio: di, dataFim: df }
    })
    resumo.value  = res.data?.resumo  || resumo.value
    detalhe.value = Array.isArray(res.data?.detalhe) ? res.data.detalhe : []
  } catch (err) {
    console.error('Erro ao carregar Aulas Experimentais => Matrículas:', err)
    resumo.value  = { agendadas: 0, compareceram: 0, matriculas: 0, percentual: 0 }
    detalhe.value = []
  } finally {
    loading.value = false
  }
}

async function fetchResumo(di, df) {
  try {
    const res = await axios.get('/gestao/relatorios/operacionais/aulas-experimentais-matriculas', {
      params: { dataInicio: di, dataFim: df }
    })
    return res.data?.resumo || null
  } catch {
    return null
  }
}

// Botão "Executar" (modo tela)
async function abrirPrint() {
  if (!dataInicioLocal.value || !dataFimLocal.value) return

  // (Opcional) Carrega KPIs na tela (sem bloquear)
  fetchResumo(dataInicioLocal.value, dataFimLocal.value).then(r => (resumoTela.value = r))

  const inicioFmt = globalUtils.formatarData(dataInicioLocal.value)
  const fimFmt    = globalUtils.formatarData(dataFimLocal.value)
  const subtitle  = `( ${inicioFmt} a ${fimFmt} )`

  router.push({
    path:  '/gestao/relatorios/print/aulas-experimentais-matriculas',
    query: {
      title:      'Aulas Experimentais => Matrículas',
      dataInicio: dataInicioLocal.value,
      dataFim:    dataFimLocal.value,
      subtitle
    }
  })
}

function formatarPercentual(v) {
  const n = Number(v) || 0
  return n.toFixed(2).replace('.', ',') + '%'
}

</script>

<style scoped>
.mb-3 { margin-bottom: 1rem; }

/* ======== KPIs (tela) ======== */
.kpi-row {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 8px;
  margin-bottom: 12px;
}
.kpi-card {
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 8px 10px;
  text-align: center;
  background: #fff;
}
.kpi-title { font-size: 12px; font-weight: 600; color: #333; margin-bottom: 4px; }
.kpi-value { font-size: 16px; font-weight: 700; color: #111; }

/* ======== Tabelas (print/tela) ======== */
.table th, .table td { font-size: 12px; }
.table thead th { white-space: nowrap; }
.text-center { text-align: center !important; }

/* Ajuste visual para a tabela de KPIs no print/tela */
.kpi-table thead th { font-weight: 700; }

/* Não imprimir elementos marcados */
@media print {
  .nao-imprimir { display: none !important; }
}
@media (max-width: 768px) {
  .kpi-row { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
</style>
